// Code generated by contracts/generate.ts. DO NOT EDIT.
//
// Edit the JSON under contracts/data/ and run `pnpm contracts:generate`.
// CI fails if this file is hand-edited or left stale.

/**
 * Where a user's Plus entitlement originated. Written server-side onto
 * users/{uid} by setPlusEntitlement alongside subscriptionStatus.
 */
export type SubscriptionProvider =
  | 'stripe'
  | 'apple_app_store'
  | 'google_play';

/** Alias used by the backend; the two must never diverge. */
export type EntitlementProvider = SubscriptionProvider;

export const SUBSCRIPTION_PROVIDERS: readonly SubscriptionProvider[] = Object.freeze([
  'stripe',
  'apple_app_store',
  'google_play',
]);

/** Providers with no recorded value are treated as this one. */
export const DEFAULT_SUBSCRIPTION_PROVIDER: SubscriptionProvider = 'stripe';

/**
 * The raw status string as reported by whichever provider sold the
 * subscription, stored verbatim for diagnostics. It is NOT a single
 * vocabulary: a value is only interpretable next to the subscriptionProvider
 * it came from, and 'active' means different things to Stripe and Apple.
 * Never derive entitlement from this — check for UserRoles.Plus instead.
 */
export type SubscriptionStatus =
  // stripe
  | 'active'
  | 'trialing'
  | 'past_due'
  | 'canceled'
  | 'unpaid'
  | 'incomplete'
  | 'incomplete_expired'
  | 'paused'
  // apple_app_store
  | 'grace_period'
  | 'billing_retry'
  | 'expired'
  | 'revoked'
  | 'unknown'
  // google_play
  | 'SUBSCRIPTION_STATE_ACTIVE'
  | 'SUBSCRIPTION_STATE_IN_GRACE_PERIOD'
  | 'SUBSCRIPTION_STATE_ON_HOLD'
  | 'SUBSCRIPTION_STATE_CANCELED'
  | 'SUBSCRIPTION_STATE_PAUSED'
  | 'SUBSCRIPTION_STATE_EXPIRED'
  | 'SUBSCRIPTION_STATE_PENDING'
  | 'SUBSCRIPTION_STATE_UNSPECIFIED';

/**
 * Per provider, the raw statuses that mean the user should be holding
 * UserRoles.Plus right now. A user whose stored status appears here but
 * whose roles lack `plus` is a real inconsistency — see
 * reconcilePlusEntitlements.
 */
export const ENTITLING_STATUSES: Readonly<
  Record<SubscriptionProvider, ReadonlySet<string>>
> = Object.freeze({
  stripe: new Set([
    'active',
    'trialing',
  ]),
  apple_app_store: new Set([
    'active',
    'grace_period',
    'billing_retry',
  ]),
  google_play: new Set([
    'SUBSCRIPTION_STATE_ACTIVE',
    'SUBSCRIPTION_STATE_IN_GRACE_PERIOD',
    'SUBSCRIPTION_STATE_ON_HOLD',
  ]),
});

/** Every entitling status across all providers, for coarse Firestore querying. */
export const ALL_ENTITLING_STATUSES: readonly string[] = Object.freeze(
  Array.from(new Set(
    Object.values(ENTITLING_STATUSES).flatMap((s) => Array.from(s)),
  )),
);

/**
 * Per provider, the statuses that mean the subscription has a billing
 * problem worth warning the user about, or it will lapse. Purely advisory:
 * this never gates access, and it is independent of ENTITLING_STATUSES —
 * Stripe's past_due warns without entitling, while Apple's grace_period does
 * both.
 */
export const NEEDS_ATTENTION_STATUSES: Readonly<
  Record<SubscriptionProvider, ReadonlySet<string>>
> = Object.freeze({
  stripe: new Set([
    'past_due',
    'unpaid',
  ]),
  apple_app_store: new Set([
    'grace_period',
    'billing_retry',
  ]),
  google_play: new Set([
    'SUBSCRIPTION_STATE_IN_GRACE_PERIOD',
    'SUBSCRIPTION_STATE_ON_HOLD',
  ]),
});
